import { useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { ApiError } from "@ferrivia/api-contract";
import { Button, Screen, Text } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import { useApi, usePasskeyDevice, useTracker } from "./AuthProvider";
import s from "./EnrolPasskey.module.css";

/** Why the prompt is up — reported to the sign-in funnel, not shown to the user. */
export type EnrolmentReason = "no_passkey" | "new_device" | "check_failed";

export interface EnrolPasskeyProps {
  reason?: EnrolmentReason;
  onDone: () => void;
  onSkip: () => void;
}

export function EnrolPasskey({ reason = "no_passkey", onDone, onSkip }: EnrolPasskeyProps) {
  const api = useApi();
  const tracker = useTracker();
  const passkeyDevice = usePasskeyDevice();
  const { t } = useTranslation("auth");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errorMessages: Record<string, string> = {
    webauthn_failed: t("enrol.error.webauthn_failed", "Laitteen rekisteröinti epäonnistui. Voit yrittää myöhemmin."),
    forbidden: t("enrol.error.forbidden", "Passkeyn rekisteröinti vaatii tuoreen pankkitunnistautumisen."),
  };
  function errorMessage(code: string): string {
    return errorMessages[code] ?? t("enrol.error.fallback", "Jokin meni pieleen. Voit yrittää myöhemmin.");
  }

  useEffect(() => {
    tracker.track("signin.enrolment_shown", { reason });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleEnrol() {
    setPending(true);
    setError(null);
    try {
      const options = await api.passkeys.registrationOptions();
      const response = await startRegistration({ optionsJSON: options });
      await api.passkeys.verifyRegistration(response);
      // Registration is the only moment transports are knowable — an assertion
      // response carries none — and naming this credential on the next sign-in
      // is what spares the person the platform's "which passkey?" sheet.
      //
      // Optional chaining on a field the type says is always there, because
      // the server has already accepted this registration by now: nothing in
      // this bookkeeping line may throw and turn a successful enrolment into
      // an error message. rememberCredential swallows storage failures for the
      // same reason.
      passkeyDevice.rememberCredential({ id: response.id, transports: response.response?.transports });
      tracker.track("signin.enrolment_accepted", {});
      onDone();
    } catch (err) {
      // The authenticator rejecting one of registrationOptions'
      // excludeCredentials is WebAuthn's way of saying "this device already
      // has a passkey for this account" — the one thing the prompt exists to
      // arrange. Nothing to enrol and nothing to report as an error; record
      // the device and move on.
      if (err instanceof Error && err.name === "InvalidStateError") {
        tracker.track("signin.enrolment_already_on_device", {});
        onDone();
        return;
      }
      setError(err instanceof ApiError ? errorMessage(err.code) : errorMessage(""));
    } finally {
      setPending(false);
    }
  }

  function handleSkip() {
    tracker.track("signin.enrolment_skipped", {});
    onSkip();
  }

  return (
    <Screen className={s.root}>
      <div className={s.content}>
        <Text as="h1" size="xl" weight="bold">
          {t("enrol.title", "Ota käyttöön passkey")}
        </Text>
        <Text as="p" className={s.description}>
          {t(
            "enrol.description",
            "Kirjaudu jatkossa sisään laitteesi tunnistuksella (sormenjälki, kasvotunnistus tai laitteen PIN-koodi) ilman pankkitunnuksia.",
          )}
        </Text>
        <div className={s.actions}>
          <Button className={s.button} disabled={pending} onClick={handleEnrol}>
            {t("enrol.enrolButton", "Ota passkey käyttöön")}
          </Button>
          <Button variant="ghost" className={s.button} disabled={pending} onClick={handleSkip}>
            {t("enrol.skipButton", "Ohita toistaiseksi")}
          </Button>
        </div>
        {error ? (
          <Text as="p" size="sm" className={s.error}>
            {error}
          </Text>
        ) : null}
      </div>
    </Screen>
  );
}
